import React, { useState } from 'react';
import {
  Card, Form, Button, Alert, Spinner, Modal, Row, Col, InputGroup, Toast,
} from 'react-bootstrap';
import { Search, AlertCircle, Copy, CheckCircle2, ShieldCheck } from 'lucide-react';
import useAuthHeaders from '../../hooks/useAuthHeaders';
import FacturaResultados from './FacturaResultados';
import RequestViewer from './RequestViewer';

const API_URL_V2 = process.env.REACT_APP_API_URL_V2;

const MESES = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre',
];

/** Opciones del filtro de complemento CFDI (ddlComplementos del portal SAT).
 *  El value coincide con el nombre del enum TipoComprobante en el backend. */
const TIPO_COMPROBANTE_OPTS = [
  { value: 'Todos',                          label: 'Todos' },
  { value: 'RecepcionPagos',                 label: 'Recepción de Pagos' },
  { value: 'ReciboPagoSalarios12',           label: 'Recibo de Pago de Salarios 1.2' },
  { value: 'Estandar',                       label: 'Estándar (sin complemento)' },
  { value: 'AcreditamientoIEPS',             label: 'Acreditamiento de IEPS' },
  { value: 'Aerolineas',                     label: 'Aerolíneas' },
  { value: 'CartaPorte',                     label: 'Carta Porte' },
  { value: 'CartaPorte20',                   label: 'Carta Porte 2.0' },
  { value: 'CartaPorte30',                   label: 'Carta Porte 3.0' },
  { value: 'CartaPorte31',                   label: 'Carta Porte 3.1' },
  { value: 'CertificadoDestruccion',         label: 'Certificado de Destrucción' },
  { value: 'ComercioExterior',               label: 'Comercio Exterior' },
  { value: 'ComercioExterior11',             label: 'Comercio Exterior 1.1' },
  { value: 'ComercioExterior20',             label: 'Comercio Exterior 2.0' },
  { value: 'CompraVentaDivisas',             label: 'Compra Venta de Divisas' },
  { value: 'ConsumoCombustibles',            label: 'Consumo de Combustibles' },
  { value: 'ConsumoCombustibles11',          label: 'Consumo de Combustibles 1.1' },
  { value: 'Donatarias',                     label: 'Donatarias' },
  { value: 'EstadoCuentaBancario',           label: 'Estado De Cuenta Bancario' },
  { value: 'EstadoCuentaCombustibles12',     label: 'Estado de Cuenta Combustibles 1.2' },
  { value: 'EstadoCuentaCombustiblesMonederos', label: 'Estado de Cuenta Combustibles Monederos' },
  { value: 'GastosHidrocarburos',            label: 'Gastos de Hidrocarburos' },
  { value: 'HidrocarburosPetroliferos',      label: 'Hidrocarburos Petroliferos' },
  { value: 'INE11',                          label: 'INE 1.1' },
  { value: 'IngresosHidrocarburos',          label: 'Ingresos de Hidrocarburos' },
  { value: 'InstitucionesEducativas',        label: 'Instituciones Educativas Privadas' },
  { value: 'LeyendasFiscales',               label: 'Leyendas Fiscales' },
  { value: 'MisCuentas',                     label: 'Mis Cuentas' },
  { value: 'NotariosPublicos',               label: 'Notarios Públicos' },
  { value: 'ObrasArtesAntiguedades',         label: 'Obras de Artes y Antigüedades' },
  { value: 'OtrosDerechosImpuestos',         label: 'Otros Derechos e Impuestos' },
  { value: 'PagoEnEspecie',                  label: 'Pago en Especie' },
  { value: 'PersonaFisicaCoordinado',        label: 'Persona Física Integrante de Coordinado' },
  { value: 'RecepcionPagosAlt',              label: 'Recepción de Pagos (versión anterior)' },
  { value: 'ReciboDonativos',                label: 'Recibo de Donativo' },
  { value: 'ReciboPagoSalarios',             label: 'Recibo de Pago de Salarios' },
  { value: 'SectorVentasDetalle',            label: 'Sector de Ventas al Detalle' },
  { value: 'ServiciosConstruccion',          label: 'Servicios de Construcción' },
  { value: 'SPEI',                           label: 'SPEI de Tercero a Tercero' },
  { value: 'SustitucionRenovacionVehicular', label: 'Sustitución y Renovación Vehicular' },
  { value: 'Terceros',                       label: 'Terceros' },
  { value: 'TercerosAlt',                    label: 'Terceros (versión 2)' },
  { value: 'TimbreFiscalDigital',            label: 'Timbre Fiscal Digital' },
  { value: 'TuristaPasajeroExtranjero',      label: 'Turista o Pasajero Extranjero' },
  { value: 'ValesDespensa',                  label: 'Vales de Despensa' },
  { value: 'VehiculoUsado',                  label: 'Vehículo Usado' },
  { value: 'VentaVehiculos',                 label: 'Venta de Vehículos' },
];

/**
 * Tab de consulta de facturas CFDI.
 * Soporta dos modos:
 *   FIEL → POST /facfiel  (FormData: llavePrivada, Certificado, Contrasena)
 *   CIEC → GET  /fac      (headers: Rfc, Secret; params: mes, anio)
 *
 * Props:
 *   - credentials : { authMode, rfc, privateKeyFile, certFile, fielPassword, secret }
 *   - isValid     : boolean
 */
const ConsultaFacturasTab = ({ credentials, isValid }) => {
  const { getToken } = useAuthHeaders();
  const isCiec = credentials.authMode === 'ciec';

  // ── Parámetros de búsqueda ─────────────────────────────
  const [requestId, setRequestId]         = useState('');
  const [searchType, setSearchType]       = useState('1');        // '1'=fecha, '0'=UUID
  const [invoiceStatus, setInvoiceStatus] = useState('-1');
  const [direction, setDirection]         = useState('recibidos');

  // FIEL: rango de fechas
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd]     = useState('');

  // CIEC: mes + año + día (0 = todos los días del mes)
  const now = new Date();
  const [mes, setMes]   = useState(now.getMonth() + 1);  // 1–12
  const [anio, setAnio] = useState(now.getFullYear());
  const [dia, setDia]   = useState(0);                   // 0=todos, 1-31

  // UUID (para búsqueda por folio)
  const [uuidSearch, setUuidSearch] = useState('');

  // Tipo de complemento CFDI
  const [tipoComprobante, setTipoComprobante] = useState('Todos');

  // ── Resultado ─────────────────────────────────────────
  const [loading, setLoading]             = useState(false);
  const [facturas, setFacturas]           = useState([]);
  const [hasQueried, setHasQueried]       = useState(false);
  const [lastRequestId, setLastRequestId] = useState('');
  const [errorMsg, setErrorMsg]           = useState('');
  const [showError, setShowError]         = useState(false);
  const [copied, setCopied]               = useState(false);
  const [lastRequest, setLastRequest]     = useState(null);

  const [vaultSaving, setVaultSaving]     = useState(false);
  const [vaultSaveOk, setVaultSaveOk]     = useState(false);
  const [vaultSaveErr, setVaultSaveErr]   = useState('');

  /** yyyy-MM-dd → 'yyyy-MM-dd HH:mm:ss' (sólo para FIEL) */
  const toApiDate = (d, isEnd) => d ? `${d} ${isEnd ? '23:59:59' : '00:00:00'}` : null;

  const vaultRegistered = credentials.signingKeyStatus?.isRegistered === true;

  const buildFielFormData = () => {
    const fd = new FormData();
    if (!vaultRegistered) {
      fd.append('llavePrivada', credentials.privateKeyFile);
      fd.append('Certificado',  credentials.certFile);
      fd.append('Contrasena',   credentials.fielPassword);
    }
    return fd;
  };

  /** Headers comunes según el modo */
  const buildHeaders = async () => {
    const token = await getToken();
    const h = { 'Authorization': `Bearer ${token}`, 'Rfc': credentials.rfc };
    if (isCiec) h['Secret'] = credentials.secret;
    return h;
  };

  // ── Búsqueda principal ────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;

    setLoading(true);
    setFacturas([]);
    setHasQueried(false);

    try {
      const params = new URLSearchParams({
        tipo:                 direction,
        tipoBusqueda:         searchType,
        estatusFactura:       invoiceStatus,
        solicitaMetadata:     'false',
        descargaComprobantes: 'false',
        tipoComprobante:      tipoComprobante,
      });
      if (requestId.trim()) params.set('requestId', requestId.trim());

      if (searchType === '1') {
        if (isCiec) {
          params.set('mes',  mes);
          params.set('anio', anio);
          params.set('dia',  dia);   // 0 = todos los días (default)
        } else {
          const fi = toApiDate(dateStart, false);
          const ff = toApiDate(dateEnd, true);
          if (fi) params.set('fecha_inicial', fi);
          if (ff) params.set('fecha_final', ff);
        }
      } else {
        if (uuidSearch.trim()) params.set('UUID', uuidSearch.trim());
      }

      const headers  = await buildHeaders();
      const endpoint = isCiec ? 'fac' : 'facfiel';

      let res;
      if (isCiec) {
        // CIEC: GET sin body
        res = await fetch(`${API_URL_V2}/Consultar/${endpoint}?${params}`, {
          method: 'GET', headers,
        });
      } else {
        // FIEL: POST con archivos en FormData
        res = await fetch(`${API_URL_V2}/Consultar/${endpoint}?${params}`, {
          method: 'POST', headers, body: buildFielFormData(),
        });
      }

      if (!res.ok) throw new Error(await res.text() || `Error HTTP ${res.status}`);

      const data = await res.json();
      if (!data.success) throw new Error(data.errorMessage || data.message || 'Consulta no exitosa.');

      setFacturas(data.comprobantes || []);
      setHasQueried(true);
      if (data.requestId) {
        setLastRequestId(data.requestId);
        setRequestId(data.requestId);
      }

      // Guardar info de la llamada para mostrarla en RequestViewer
      setLastRequest({
        method:      isCiec ? 'GET' : 'POST',
        baseUrl:     `${API_URL_V2}/Consultar/${endpoint}`,
        queryParams: params.toString(),
        headers: {
          'Rfc': credentials.rfc,
          ...(isCiec ? { 'Secret': credentials.secret } : {}),
        },
        formFields: (isCiec || vaultRegistered) ? null : [
          { name: 'llavePrivada', type: 'file', fileName: credentials.privateKeyFile?.name || 'llave.key', varName: 'llavePrivadaFile' },
          { name: 'Certificado',  type: 'file', fileName: credentials.certFile?.name || 'certificado.cer', varName: 'certificadoFile' },
          { name: 'Contrasena',   type: 'text', value: '{contrasena_fiel}' },
        ],
      });

      // Guardar FIEL en vault si el usuario lo solicitó
      if (!isCiec && !vaultRegistered && credentials.saveToVault && credentials.onStoreSigningKey) {
        setVaultSaving(true);
        setVaultSaveOk(false);
        setVaultSaveErr('');
        const result = await credentials.onStoreSigningKey();
        setVaultSaving(false);
        if (result.success) {
          setVaultSaveOk(true);
        } else {
          setVaultSaveErr(result.error || 'No se pudo guardar la FIEL en Key Vault.');
        }
      }

    } catch (err) {
      setErrorMsg(err.message || 'Error al consultar facturas.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  // ── Descarga XML lazy por UUID ─────────────────────────
  const handleDownloadXml = async (factura) => {
    const params = new URLSearchParams({
      tipo:                 direction,
      tipoBusqueda:         '0',           // búsqueda por UUID
      UUID:                 factura.uuid,
      estatusFactura:       '-1',
      solicitaMetadata:     'false',
      descargaComprobantes: 'true',
    });
    if (lastRequestId) params.set('requestId', lastRequestId);

    const headers  = await buildHeaders();
    const endpoint = isCiec ? 'fac' : 'facfiel';

    let res;
    if (isCiec) {
      res = await fetch(`${API_URL_V2}/Consultar/${endpoint}?${params}`, {
        method: 'GET', headers,
      });
    } else {
      res = await fetch(`${API_URL_V2}/Consultar/${endpoint}?${params}`, {
        method: 'POST', headers, body: buildFielFormData(),
      });
    }

    if (!res.ok) throw new Error(await res.text() || `Error HTTP ${res.status}`);
    const data = await res.json();
    if (!data.success) throw new Error(data.errorMessage || 'No se pudo descargar el XML.');

    const comp = (data.comprobantes || [])[0];
    if (!comp?.content) throw new Error('El comprobante no tiene XML disponible.');

    const bytes = new Uint8Array([...atob(comp.content)].map(c => c.charCodeAt(0)));
    const blob  = new Blob([bytes], { type: 'application/xml' });
    const url   = URL.createObjectURL(blob);
    const link  = Object.assign(document.createElement('a'), {
      href: url, download: comp.fileName || `${factura.uuid}.xml`,
    });
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ── Descarga PDF lazy por UUID ─────────────────────────
  /** Igual que XML pero con descargaPdfs=true y extrae pdfContent */
  const handleDownloadPdf = async (factura) => {
    const params = new URLSearchParams({
      tipo:                 direction,
      tipoBusqueda:         '0',
      UUID:                 factura.uuid,
      estatusFactura:       '-1',
      solicitaMetadata:     'false',
      descargaComprobantes: 'false',
      descargaPdfs:         'true',
    });
    if (lastRequestId) params.set('requestId', lastRequestId);

    const headers  = await buildHeaders();
    const endpoint = isCiec ? 'fac' : 'facfiel';

    let res;
    if (isCiec) {
      res = await fetch(`${API_URL_V2}/Consultar/${endpoint}?${params}`, {
        method: 'GET', headers,
      });
    } else {
      res = await fetch(`${API_URL_V2}/Consultar/${endpoint}?${params}`, {
        method: 'POST', headers, body: buildFielFormData(),
      });
    }

    if (!res.ok) throw new Error(await res.text() || `Error HTTP ${res.status}`);
    const data = await res.json();
    if (!data.success) throw new Error(data.errorMessage || 'No se pudo descargar el PDF.');

    const comp = (data.comprobantes || [])[0];
    if (!comp?.pdfContent) throw new Error('El comprobante no tiene PDF disponible.');

    const bytes = new Uint8Array([...atob(comp.pdfContent)].map(c => c.charCodeAt(0)));
    const blob  = new Blob([bytes], { type: 'application/pdf' });
    const url   = URL.createObjectURL(blob);
    const link  = Object.assign(document.createElement('a'), {
      href: url, download: `${factura.uuid}.pdf`,
    });
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const copyRequestId = async () => {
    try { await navigator.clipboard.writeText(lastRequestId); } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Render ─────────────────────────────────────────────
  return (
    <>
      <Card className="tab-section-card border-0 shadow-sm">
        <Card.Body>
          <Form onSubmit={handleSubmit}>

            {/* ── requestId ─────────────────────────── */}
            <Row className="mb-3">
              <Col md={8}>
                <Form.Label className="form-label-modern">
                  Request ID{' '}
                  <span className="text-muted fw-normal small">
                    (opcional — reutiliza una sesión SAT existente)
                  </span>
                </Form.Label>
                <InputGroup>
                  <Form.Control className="form-control-modern" type="text"
                    placeholder="Pega el requestId de una consulta anterior"
                    value={requestId} onChange={(e) => setRequestId(e.target.value)}
                    style={{ borderRadius: '10px 0 0 10px' }} />
                  {requestId && (
                    <Button variant="outline-secondary" onClick={() => setRequestId('')}
                      style={{ borderRadius: '0 10px 10px 0' }}>✕</Button>
                  )}
                </InputGroup>
              </Col>
            </Row>

            {/* ── Parámetros ────────────────────────── */}
            <div className="p-3 mb-3" style={{
              background: 'linear-gradient(135deg,rgba(19,160,255,.05),rgba(0,212,170,.05))',
              borderRadius: 12, border: '1px solid rgba(19,160,255,.12)',
            }}>
              <Row className="g-3">

                {/* Tipo de búsqueda */}
                <Col md={4}>
                  <Form.Label className="form-label-modern">Tipo de búsqueda</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check type="radio" id="search-fecha" label="Por fecha"
                      className="custom-radio" checked={searchType === '1'}
                      onChange={() => setSearchType('1')} />
                    <Form.Check type="radio" id="search-uuid" label="Por UUID"
                      className="custom-radio" checked={searchType === '0'}
                      onChange={() => setSearchType('0')} />
                  </div>
                </Col>

                {/* Dirección */}
                <Col md={4}>
                  <Form.Label className="form-label-modern">Tipo de comprobante</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check type="radio" id="dir-recibidos" label="Recibidos"
                      className="custom-radio" checked={direction === 'recibidos'}
                      onChange={() => setDirection('recibidos')} />
                    <Form.Check type="radio" id="dir-emitidos" label="Emitidos"
                      className="custom-radio" checked={direction === 'emitidos'}
                      onChange={() => setDirection('emitidos')} />
                  </div>
                </Col>

                {/* Estado */}
                <Col md={4}>
                  <Form.Label className="form-label-modern">Estado</Form.Label>
                  <Form.Select className="form-select-modern" value={invoiceStatus}
                    onChange={(e) => setInvoiceStatus(e.target.value)}>
                    <option value="-1">Todos</option>
                    <option value="1">Vigente</option>
                    <option value="0">Cancelada</option>
                  </Form.Select>
                </Col>

                {/* Complemento + campos condicionales — misma fila */}
                {searchType === '1' ? (
                  isCiec ? (
                    /* CIEC: complemento + mes + año + día */
                    <>
                      <Col md={4}>
                        <Form.Label className="form-label-modern">Complemento</Form.Label>
                        <Form.Select className="form-select-modern" value={tipoComprobante}
                          onChange={(e) => setTipoComprobante(e.target.value)}>
                          {TIPO_COMPROBANTE_OPTS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col md={3}>
                        <Form.Label className="form-label-modern">Mes</Form.Label>
                        <Form.Select className="form-select-modern" value={mes}
                          onChange={(e) => setMes(e.target.value)}>
                          {MESES.map((m, i) => (
                            <option key={i + 1} value={i + 1}>{m}</option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col md={2}>
                        <Form.Label className="form-label-modern">Año</Form.Label>
                        <Form.Control className="form-control-modern" type="number"
                          min="2000" max="2099" value={anio}
                          onChange={(e) => setAnio(e.target.value)} />
                      </Col>
                      <Col md={3}>
                        <Form.Label className="form-label-modern">Día</Form.Label>
                        <Form.Select className="form-select-modern" value={dia}
                          onChange={(e) => setDia(e.target.value)}>
                          <option value={0}>Todos</option>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </Form.Select>
                      </Col>
                    </>
                  ) : (
                    /* FIEL: complemento + fecha inicio + fecha fin */
                    <>
                      <Col md={4}>
                        <Form.Label className="form-label-modern">Complemento</Form.Label>
                        <Form.Select className="form-select-modern" value={tipoComprobante}
                          onChange={(e) => setTipoComprobante(e.target.value)}>
                          {TIPO_COMPROBANTE_OPTS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col md={4}>
                        <Form.Label className="form-label-modern">Fecha inicio</Form.Label>
                        <Form.Control className="form-control-modern" type="date"
                          value={dateStart} onChange={(e) => setDateStart(e.target.value)} />
                        {dateStart && (
                          <Form.Text className="text-muted" style={{ fontSize: '.75rem' }}>
                            Se enviará como: {dateStart} 00:00:00
                          </Form.Text>
                        )}
                      </Col>
                      <Col md={4}>
                        <Form.Label className="form-label-modern">Fecha fin</Form.Label>
                        <Form.Control className="form-control-modern" type="date"
                          value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} />
                        {dateEnd && (
                          <Form.Text className="text-muted" style={{ fontSize: '.75rem' }}>
                            Se enviará como: {dateEnd} 23:59:59
                          </Form.Text>
                        )}
                      </Col>
                    </>
                  )
                ) : (
                  /* UUID: complemento + UUID */
                  <>
                    <Col md={4}>
                      <Form.Label className="form-label-modern">Complemento</Form.Label>
                      <Form.Select className="form-select-modern" value={tipoComprobante}
                        onChange={(e) => setTipoComprobante(e.target.value)}>
                        {TIPO_COMPROBANTE_OPTS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={8}>
                      <Form.Label className="form-label-modern">UUID del comprobante</Form.Label>
                      <Form.Control className="form-control-modern" type="text"
                        placeholder="Ej. 0150BD8F-EC7D-1E44-9242-B8B0339F5BC5"
                        value={uuidSearch}
                        onChange={(e) => setUuidSearch(e.target.value.toUpperCase())}
                        style={{ fontFamily: 'monospace' }} />
                    </Col>
                  </>
                )}

              </Row>
            </div>

            {!isValid && (
              <Alert variant="warning" className="d-flex align-items-center py-2 mb-3">
                <AlertCircle size={16} className="me-2 flex-shrink-0" />
                <small>Completa las credenciales de arriba para habilitar la consulta.</small>
              </Alert>
            )}

            <Button type="submit" className="btn-gradient" disabled={!isValid || loading}>
              {loading
                ? <><Spinner animation="border" size="sm" className="me-2" />Consultando…</>
                : <><Search size={18} />Consultar Facturas</>
              }
            </Button>

          </Form>

          {/* ── Banner requestId ─────────────────── */}
          {lastRequestId && (
            <div className="mt-3 p-3 d-flex align-items-center gap-3 flex-wrap"
              style={{ background: 'rgba(0,212,170,.07)',
                border: '1px solid rgba(0,212,170,.3)', borderRadius: 10 }}>
              <CheckCircle2 size={18} color="#00d4aa" className="flex-shrink-0" />
              <div className="flex-grow-1">
                <div className="text-muted small mb-1">
                  Request ID — se reutilizará automáticamente en la próxima consulta:
                </div>
                <code style={{ fontSize: '.85rem', color: '#1a1f3a',
                  wordBreak: 'break-all', background: 'transparent' }}>
                  {lastRequestId}
                </code>
              </div>
              <Button variant="outline-success" size="sm" onClick={copyRequestId}
                className="d-flex align-items-center gap-1 flex-shrink-0">
                {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                {copied ? 'Copiado' : 'Copiar'}
              </Button>
            </div>
          )}

          <RequestViewer request={lastRequest} />

          {hasQueried && (
            <FacturaResultados
              facturas={facturas}
              onDownloadXml={handleDownloadXml}
              onDownloadPdf={handleDownloadPdf}
              onClear={() => { setFacturas([]); setHasQueried(false); setLastRequestId(''); }}
            />
          )}
        </Card.Body>
      </Card>

      {/* ── Vault save feedback ─────────────────── */}
      <Toast show={vaultSaving} className="custom-toast"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1100 }}>
        <Toast.Header closeButton={false}>
          <Spinner animation="border" size="sm" className="me-2" />
          <strong className="me-auto">Guardando en Key Vault…</strong>
        </Toast.Header>
      </Toast>

      <Toast show={vaultSaveOk} onClose={() => setVaultSaveOk(false)} delay={5000} autohide
        className="custom-toast toast-success"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1100 }}>
        <Toast.Header>
          <ShieldCheck size={14} className="me-2 text-success" />
          <strong className="me-auto">FIEL guardada en Key Vault</strong>
        </Toast.Header>
        <Toast.Body>Ya no necesitarás subir los archivos FIEL para este RFC.</Toast.Body>
      </Toast>

      <Toast show={!!vaultSaveErr} onClose={() => setVaultSaveErr('')} delay={7000} autohide
        className="custom-toast toast-error"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1100 }}>
        <Toast.Header>
          <AlertCircle size={14} className="me-2 text-danger" />
          <strong className="me-auto">No se pudo guardar en Key Vault</strong>
        </Toast.Header>
        <Toast.Body style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{vaultSaveErr}</Toast.Body>
      </Toast>

      {/* ── Modal error ─────────────────────────── */}
      <Modal show={showError} onHide={() => setShowError(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: '3px solid #ff6b6b' }}>
          <Modal.Title className="d-flex align-items-center gap-2" style={{ color: '#1a1f3a' }}>
            <AlertCircle size={20} color="#ff6b6b" />
            Error al consultar facturas
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0" style={{ color: '#4a5568', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {errorMsg}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowError(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ConsultaFacturasTab;
